import { Avatar, Row } from 'antd';
import { ICalendarHeader } from 'features/appointment/services/types/calendar';
import { memo } from 'react';
import { useAppDispatch } from 'store/hooks';
import appointmentActions from 'features/appointment/services/actions';
import { APPOINTMENT_LAYOUT, CALENDAR_VIEW_TYPE } from 'features/appointment/services/constants';
import AnyBodyImage from 'components/AnyBodyImage';
import { get } from 'lodash';
import add_icon from 'assets/Icons/appointments/Add.svg';
import minus_icon from 'assets/Icons/appointments/Minus.svg';
import styled from 'styled-components';
import { modalConfirmRef } from './hooks/useCalendarFilter';
import Box from 'components/Box';
import settingStaffActions from 'features/settingStaff/services/actions';
import { useAppNavigate } from 'hooks/useAppNavigate';
import { verifyPinCode } from 'widgets/RequestAdminLocalPW/Callback';
import { FeaturePermission } from 'constants/Permission';

type HeaderItemProps = {
  item: ICalendarHeader<any>;
  typeView: CALENDAR_VIEW_TYPE;
};

const HeaderItem = ({ item, typeView }: HeaderItemProps) => {
  const dispatch = useAppDispatch();
  const { appNavigate } = useAppNavigate();
  const onAddAnybody = () => dispatch(appointmentActions.addAnybody());
  const onRemoveAnybody = (staff_id: string) => () =>
    modalConfirmRef.current?.open('Are you sure you want to remove this staff?', () =>
      dispatch(appointmentActions.removeAnybody(staff_id))
    );

  const handleClickAvatar = (o: ICalendarHeader<any>) => () => {
    dispatch(appointmentActions.setAppointmentLayout(APPOINTMENT_LAYOUT.LIST));
    dispatch(appointmentActions.setTableParams({ staffId: o.id, page: 1 }));
  };

  const handleClickScheduleStaff = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    verifyPinCode(() => {
      dispatch(settingStaffActions.getSelectedStaffInfo.fetch(item.id));
      appNavigate('setting-staff/schedule');
    }, false, FeaturePermission.SCHEDULE_STAFF);
  };

  if (typeView === CALENDAR_VIEW_TYPE.WEEK_VIEW) {
    return (
      <>
        <p className='subtext'>{item?.value?.format('ddd, MMMM DD')}</p>
      </>
    );
  }

  return (
    <Container
      align={'middle'}
      justify={'center'}
      className='staff-th-container'
      style={{
        gap: '10px',
        position: 'relative',
        paddingRight: item.anybody ? '30px' : 0,
        paddingTop: 4,
      }}
    >
      <Box
        display='flex'
        flexDirection='column'
        gap='2'
        onClick={handleClickAvatar(item)}
        style={{ cursor: 'pointer' }}
      >
        {item.anybody ? (
          <AnyBodyImage width={80} height={80} />
        ) : (
          <Avatar
            key={item.id}
            size={80}
            src={item.url}
            style={{ backgroundColor: '#f56a00', fontSize: '32px' }}
          >
            {get(item.label, [0], '')}
          </Avatar>
        )}
        <p className='subtext'>{item.label}</p>
      </Box>
      {!item.anybody && (
        <ScheduleStaffBox className='schedule-staff-box' onClick={handleClickScheduleStaff}>
          {staff_schedule_icon}
        </ScheduleStaffBox>
      )}
      {item.anybody &&
        (item.addAnybody ? (
          <ButtonAddMinus className='ButtonAddMinus' onClick={onAddAnybody}>
            <img src={add_icon} alt='add_icon' />
          </ButtonAddMinus>
        ) : (
          <ButtonAddMinus className='ButtonAddMinus' onClick={onRemoveAnybody(item.id)}>
            <img src={minus_icon} alt='minus_icon' />
          </ButtonAddMinus>
        ))}
    </Container>
  );
};

export default memo(HeaderItem);
const Container = styled(Row)`
  padding: 8px 0;
  .subtext {
    background: #fff;
  }
`;

const ButtonAddMinus = styled.button`
  display: flex;
  width: 40px;
  height: 40px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  position: absolute;
  right: 6.333px;
  border-radius: 4px;
  background: #e4f4f6;
  img {
    width: 32px;
    height: 32px;
  }

  &:hover,
  &:active {
    background: #eee;
  }
`;

const ScheduleStaffBox = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  background: #fff;
  position: absolute;
  right: 4px;
  top: 4px;
  &:hover {
    background: #f5f5f5;
  }
`;

const staff_schedule_icon = (
  <svg xmlns='http://www.w3.org/2000/svg' width='28' height='29' viewBox='0 0 28 29' fill='none'>
    <path
      d='M11.4818 1.0979C14.0682 0.583435 16.7496 0.847543 19.1859 1.85669C21.6222 2.86585 23.7042 4.5752 25.1693 6.76782C26.6344 8.96042 27.4163 11.538 27.4164 14.175C27.4164 17.7113 26.0116 21.1033 23.5111 23.6038C21.0107 26.104 17.6194 27.509 14.0834 27.509C13.7846 27.509 13.4866 27.4967 13.1898 27.4768V25.7317C13.1898 25.6766 13.1872 25.6216 13.1859 25.5667C14.9779 25.7078 16.785 25.4264 18.4574 24.7336C20.5455 23.8686 22.3306 22.404 23.5863 20.5247C24.842 18.6453 25.5121 16.4354 25.5121 14.175C25.512 11.1441 24.3077 8.23722 22.1644 6.09399C20.0212 3.95081 17.1143 2.74634 14.0834 2.74634C11.8231 2.74636 9.61316 3.41733 7.73376 4.6731C5.85454 5.92887 4.38972 7.71385 3.52478 9.802C2.94096 11.2116 2.6487 12.7166 2.65564 14.2278C2.22559 14.7752 1.92621 15.4164 1.78943 16.1038C1.68186 16.6446 1.54152 17.1565 1.4906 17.8088C0.659322 16.0168 0.607348 13.5801 1.00623 11.5745C1.5207 8.98805 2.79094 6.61201 4.65564 4.74731C6.52024 2.88274 8.89558 1.61242 11.4818 1.0979ZM15.0355 13.7756L19.7982 18.5471L18.4545 19.8899L13.1312 14.5657V5.60376H15.0355V13.7756Z'
      fill='#C84B31'
    />
    <ellipse cx='6.22768' cy='16.8059' rx='2.67483' ry='2.67483' fill='#C84B31' />
    <path
      d='M0.878052 28.174H11.5774L11.2802 23.9506L10.0913 22.1908L6.82211 21.4869L3.55288 21.8389L1.17525 24.3025L0.878052 28.174Z'
      fill='#C84B31'
    />
    <path
      d='M6.22781 20.4837C5.56652 20.4837 4.92009 20.2876 4.37024 19.9202C3.8204 19.5528 3.39185 19.0306 3.13879 18.4197C2.88573 17.8087 2.81951 17.1365 2.94852 16.4879C3.07753 15.8393 3.39598 15.2435 3.86358 14.7759C4.33118 14.3083 4.92694 13.9899 5.57552 13.8609C6.2241 13.7319 6.89638 13.7981 7.50733 14.0511C8.11828 14.3042 8.64047 14.7328 9.00786 15.2826C9.37525 15.8324 9.57134 16.4789 9.57134 17.1402C9.57134 18.0269 9.21908 18.8774 8.59205 19.5044C7.96501 20.1314 7.11457 20.4837 6.22781 20.4837ZM6.22781 15.134C5.83104 15.134 5.44318 15.2517 5.11327 15.4721C4.78337 15.6926 4.52624 16.0059 4.3744 16.3725C4.22256 16.739 4.18283 17.1424 4.26024 17.5315C4.33765 17.9207 4.52871 18.2781 4.80927 18.5587C5.08983 18.8393 5.44729 19.0303 5.83644 19.1077C6.22559 19.1851 6.62895 19.1454 6.99552 18.9936C7.36209 18.8417 7.6754 18.5846 7.89584 18.2547C8.11627 17.9248 8.23393 17.5369 8.23393 17.1402C8.23393 16.6081 8.02257 16.0978 7.64635 15.7216C7.27013 15.3454 6.75987 15.134 6.22781 15.134Z'
      fill='#C84B31'
    />
    <path
      d='M11.6444 28.8428H0.811309C0.651692 28.8428 0.498612 28.7724 0.385746 28.647C0.27288 28.5216 0.209473 28.3515 0.209473 28.1741V26.168C0.209473 24.7492 0.716733 23.3885 1.61966 22.3852C2.52259 21.382 3.74723 20.8184 5.02416 20.8184H7.4315C8.70844 20.8184 9.93307 21.382 10.836 22.3852C11.7389 23.3885 12.2462 24.7492 12.2462 26.168V28.1741C12.2462 28.3515 12.1828 28.5216 12.0699 28.647C11.9571 28.7724 11.804 28.8428 11.6444 28.8428ZM1.41314 27.5054H11.0425V26.168C11.0425 25.1039 10.6621 24.0834 9.98488 23.3309C9.30768 22.5785 8.3892 22.1558 7.4315 22.1558H5.02416C4.06646 22.1558 3.14798 22.5785 2.47079 23.3309C1.79359 24.0834 1.41314 25.1039 1.41314 26.168V27.5054Z'
      fill='#C84B31'
    />
  </svg>
);
